import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { getSeats, updateSeats, getMembers } from 'api/account';

import APFormGroup from 'components/FormFields/APFormGroup';
import InputField from 'components/FormFields/InputField';
import APModal from 'components/Modal/APModal';
import { FadeLoader } from 'react-spinners';
import { styledTheme } from 'globalStyles/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as actions from 'containers/Billing/actions';
import * as projectsActions from 'containers/Projects/actions';
import PaymentModal from 'pages/Billing/Payment';
import SeatsModal from 'pages/Billing/ManageSeats';
import HorizontalMenu from 'components/Menus/HorizontalMenu';
import { convertCurrency } from 'utils/general';
import clsx from 'clsx';

import PlanCard from './PlanCard/index';
import { BillingSummary } from './BillingSummary/BillingSummary';

import './billing.scss';

const MONTHLY = 'monthly';
const ANNUALLY = 'annually';

export const formatDate = (date) => {
  if (!date) return null;
  const formatted = dayjs(date).format('MMMM DD, YYYY');
  // Debug: log if we get "Invalid Date"
  if (formatted === 'Invalid Date') {
    /* eslint-disable-next-line no-console */
    console.warn('formatDate received invalid date:', date);
    return null;
  }
  return formatted;
};

const Billing = () => {
  const history = useHistory();
  const billing = useSelector((state) => state.billing);
  const userInfo = useSelector((state) => state.global.userInfo);
  const projectsLimit = useSelector((state) => state.projects.projectsLimit);
  const {
    info: billingInfo,
    promoCodeError,
    startTrialError,
    invoicePreviews,
    paymentMethodUpdating,
    subscriptionUpdating,
    subscriptionPauseUpdating,
    subscribeError,
    hasPendingPaymentMethod,
  } = billing;

  const { isDisabled, billing: userBilling, personas, orgId } = userInfo;
  const { isOrgMember, isOrgOwner, isClientVendor } = personas || {};
  const { isDelinquent, planningSubscription: subscription } = userBilling || {};

  const {
    invoice: currentInvoice,
    proPlans,
    plansByCode,
    // stripePauseSuscriptionDays,
    allPaymentIntents, // List is filtered by the api to only include intents with next action.
  } = billingInfo || {};
  const [invoice, setInvoice] = useState(null);
  const { startingBalance = 0, total: invoiceTotal = 0 } = invoice || {};

  const {
    name: planName,
    isTrial,
    isPromo,
    isEnded,
    endedAt,
    expiresAt,
    isExpired,
    code: currentPlanCode,
    remainingTrialDays,
  } = subscription || {};

  // Current subscription doesn't include number of projects, so we manually get it from the complete list of plans.
  const currentNumberOfProjects = (plansByCode && currentPlanCode in plansByCode) &&
    plansByCode[currentPlanCode].weddings;
  const currentInterval = (plansByCode && currentPlanCode in plansByCode) &&
    plansByCode[currentPlanCode].renew;

  const invoiceDate = invoice?.date || '';
  // Churned accounts are cancelled, but forcibly instead of by user choice.
  const isCancel = (!subscription && ((userInfo?.isCancel && userInfo?.cancelAt)) || (isEnded && endedAt));
  // Pending cancellation: user-initiated cancel with access remaining until end of billing period.
  // Guard against isCancel (churned) overlap — both can have a future cancelAt.
  const isPendingCancellation = !isCancel && !!userInfo?.cancelAt && dayjs(userInfo.cancelAt).isAfter(dayjs());

  // Get pending payment method information (e.g. ACH manual verification).
  const pendingMicrodeposit = hasPendingPaymentMethod ?
    allPaymentIntents.filter(pi => pi.nextAction?.type === 'verify_with_microdeposits') : [];
  const isAwaitingMicrodeposit = !!pendingMicrodeposit.length;

  const redeemedPromo = userInfo?.promos?.[0];
  const redeemedPromoCode = redeemedPromo?.stripeCouponIdentifier
    || redeemedPromo?.proSubscriptionCode
    || redeemedPromo?.indSubscriptionCode
    || '';

  const [promoReferralCode, setPromoReferralCode] = useState('');
  const [showPromoReferralCodeError, setPromoReferralCodeError] = useState(false);
  const [isStartingTrial, setStartingTrial] = useState(false);
  const [modalPaymentIsOpen, setModalPaymentIsOpen] = useState(false);
  const [seatsModalIsOpen, setSeatsModalIsOpen] = useState(false);
  const [seatsUpdated, setSeatsUpdated] = useState(false);
  const [proPlanTab, setProPlanTab] = useState(MONTHLY);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [updatingPaymentMethod, setUpdatingPaymentMethod] = useState(false);
  const [subscriptionConfirmationModalOpened, setSubscriptionConfirmationModalOpened] = useState(false);
  const [pauseSubscriptionModalOpened, setPauseSubscriptionModalOpened] = useState(false);
  const [cancelSubscriptionModalOpened, setCancelSubscriptionModalOpened] = useState(false);
  const [trialConversionConfirmationModalOpened, setTrialConversionModalOpened] = useState(false);
  const [cancelTrialConfirmationModalOpened, setCancelTrialConfirmationModalOpened] = useState(false);
  const [summaryScrollEnabled, setSummaryScrollEnabled] = useState(false);
  const [billingCycle, setBillingCycle] = useState(null);
  const [seatsDiff, setSeatsDiff] = useState(null);
  const [additionalSeats, setAdditionalSeats] = useState(null);
  const [subscriptionConfirmationText, setSubscriptionConfirmationText] = useState('');

  const [errorModalOpened, setErrorModalOpened] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState(false);

  const currentPlan = (subscription?.code && !subscription?.isEnded && plansByCode &&
    Object.prototype.hasOwnProperty.call(plansByCode, subscription.code)) ?
      plansByCode[subscription.code] : null;
  // Plan change is if user is subscribed and looking at a plan different than subscribed plan.
  // Or if user is cancelled, disabled, or not subscribed and looking at a plan.
  const isPlanChange = (selectedPlan && selectedPlan?.price !== 0 && subscription?.code !== selectedPlan?.code);
  // Currently we reset billing cycle differently if changing intervals.
  const isIntervalChange = currentPlan?.renew !== selectedPlan?.renew;
  const isUpgrade = isPlanChange && selectedPlan?.price > currentPlan?.price;
  // Show billing summary if any of these are true.
  // The user has a subscription AND none of these apply to them:
  // paused, canceling, pending cancellation, on a trial, on a promo, or is a client/vendor.
  // OR — the user isn't paused, has a selected plan, and isn't pending cancellation.
  // OR — the user is selecting a plan different from the current plan..
  const showBillingSummary = (
      !!subscription
      && (!userInfo?.isPaused && !isCancel && !isPendingCancellation && !isTrial && !isPromo && !isClientVendor)
    )
    || (!userInfo?.isPaused && selectedPlan && !isPendingCancellation)
    || isPlanChange;

  const showPaymentMethod = userBilling?.hasPaymentMethod;
  const showPromoCode = isOrgOwner && isTrial;
  const showPauseSubscription = !!subscription && !isTrial &&
    !isCancel && !isPendingCancellation && !isPromo && !isClientVendor;
  const showTrialConversion = isTrial && currentInvoice;
  const showBillingFooter = !!subscription &&
    ((!isTrial || selectedPlan) && (!isPromo || selectedPlan) &&
    !isCancel && (!isClientVendor || selectedPlan));
  const showManageSeats =
    !userInfo?.isPaused && !isCancel && !isPendingCancellation &&
    !isClientVendor && !(isTrial && isExpired) && !!subscription;
  const showNoPlanningSubscription = !subscription && !isCancel;
  const proPlansRef = useRef(null);
  const summaryRef = useRef(null);

  const dispatch = useDispatch();
  const { getBillingInfo, getInvoicePreviews, addPromoOrReferral, cancelTrial, trial, subscribe,
    pauseSubscription, renewSubscription, unsubscribe, resumeSubscription } = bindActionCreators(actions, dispatch);
  const { getProjectLimits } = bindActionCreators(projectsActions, dispatch);

  const [planPrice, setPlanPrice] = useState(selectedPlan?.price || 0);
  const creditApplied = convertCurrency(startingBalance < 0 && startingBalance <= invoiceTotal
    ? startingBalance
    : startingBalance - invoiceTotal);

  const tabs = [
    {
      id: MONTHLY,
      name: MONTHLY,
      onClick: () => setProPlanTab(MONTHLY),
      active: proPlanTab === MONTHLY,
    },
    {
      id: ANNUALLY,
      name: ANNUALLY,
      onClick: () => setProPlanTab(ANNUALLY),
      active: proPlanTab === ANNUALLY,
    },
  ];

  useEffect(() => {
    getBillingInfo();
    getProjectLimits();
  }, []);

  useEffect(() => {
    if (promoCodeError) {
      setPromoReferralCodeError(true);
    }
  }, [promoCodeError]);

  useEffect(() => {
    if (startTrialError) {
      setStartingTrial(false);
      setErrorModalOpened(true);
      setErrorModalMessage(`Sorry, we couldn't start your free trial.
                                Please try again in a few moments or contact support.`);
    }
  }, [startTrialError]);

  useEffect(() => {
    if (subscribeError) {
      setSubscriptionConfirmationModalOpened(false);
      setErrorModalOpened(true);
      setErrorModalMessage(`Sorry, but we couldn't complete the subscribe operation.
                                Please try again.`);
    }
  }, [subscribeError]);

  useEffect(() => {
    if (subscription && plansByCode) {
      const billingProPlan = plansByCode[subscription.code];

      if (!isCancel && !isPendingCancellation) setSelectedPlan(billingProPlan);
      setProPlanTab(billingProPlan?.renew === 'year' ? ANNUALLY : MONTHLY);
    } else {
      setSelectedPlan(null);
      setProPlanTab(MONTHLY);
    }
    if (currentInvoice) {
      setInvoice(currentInvoice);

      // Assume that current invoice has only 1 line which is the current subscription.
      // If other line items are added to the invoice, then this logic will need to change.
      if (currentInvoice.lines[0]) {
        setBillingCycle({
          start: formatDate(currentInvoice.lines[0].start),
          end: formatDate(currentInvoice.lines[0].end),
        });
      }
    }
  }, [userBilling, billingInfo, currentInvoice, userInfo]);

  useEffect(() => {
    if (subscription) {
      getInvoicePreviews();
    }
  }, [subscription]);

  useEffect(() => {
    setPlanPrice(selectedPlan ? selectedPlan.price : 0);

    if (summaryScrollEnabled) {
      summaryRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedPlan]);

  useEffect(() => {
    if (process.env.REACT_APP_PROFITWELL_ENABLED === 'true' && userInfo.email) {
      // eslint-disable-next-line no-undef
      profitwell('start', { 'user_email': userInfo.email });
    }
  }, [userInfo]);

  // If new plan is selected before previews finish fetching, load when it complete.
  useEffect(() => {
    if (invoicePreviews) {
      const { code } = selectedPlan || {};
      if (code && Object.prototype.hasOwnProperty.call(invoicePreviews, code)) {
        setInvoice(invoicePreviews[code]);
      }
    }
  }, [invoicePreviews]);

  const openModalPayment = () => {
    setModalPaymentIsOpen(true);
  };

  const handleSeatsUpdate = () => {
    // Update billing summary after updating seats.
    getBillingInfo();
    setSeatsModalIsOpen(false);
    setSeatsUpdated(true);
  };

  const isPlanLimitExceeded = () => {
    if (projectsLimit.activeWeddingCount > selectedPlan.weddings) {
      const exceededLimit = projectsLimit.activeWeddingCount - selectedPlan.weddings;
      const message = isTrial
        ? `Oops! It looks like you have more active projects than your selected plan allows.
         To confirm your subscription, you'll need to select a different plan or archive at
         least ${exceededLimit} project(s).`
        : `Oops! It looks like you have more active projects than your selected plan allows.
         You'll need to archive at least ${exceededLimit} project(s) to downgrade.`;
      setErrorModalMessage(message);
      setErrorModalOpened(true);
      return true;
    }
    return false;
  };

  const onSubscribe = () => {
    // We want to prevent users from subscribing to a current plan, but allow it if the account
    // is delinquent (failed payment).
    if (selectedPlan && (isDelinquent || selectedPlan.code !== subscription?.code || isCancel)) {

      // Check if the number of active projects exceeds the selected plan's limit
      if (isPlanLimitExceeded()) {
        return;
      };

      if (userBilling?.hasPaymentMethod) {
        planSubscribe();
      } else {
        setUpdatingPaymentMethod(true);
        setModalPaymentIsOpen(true);
      }
    }
  };

  const planSubscribe = () => {
    subscribe({ weddings: selectedPlan.weddings, renew: selectedPlan.renew });
    setSubscriptionConfirmationModalOpened(true);
  };

  const convertTrial = () => {
    // This could just be a hack
    // I'm not entirely sure if we need to set the
    // trial_period_days in the database before this
    subscribe({ weddings: selectedPlan.weddings, renew: selectedPlan.renew, convert: true });
    setSubscriptionConfirmationModalOpened(true);
  };

  useEffect(() => {
    if (!paymentMethodUpdating && updatingPaymentMethod) {
      setUpdatingPaymentMethod(false);
      onSubscribe();
    }
  }, [paymentMethodUpdating]);

  const onPauseSubscription = () => {
    if (userInfo?.isPaused) {
      renewSubscription();
    } else {
      setPauseSubscriptionModalOpened(true);
    };
  };

  const onPauseAccount = () => {
    pauseSubscription();
    setPauseSubscriptionModalOpened(false);
  };

  const startTrial = () => {
    // Check if user has a payment method set up
    const hasPaymentMethod = userInfo?.billing?.hasPaymentMethod;

    // Check if user is a planning partner by looking at login weddings.
    const loginWeddings = userInfo?.weddings;

    // Simplified subscription validation - if user has any subscription record,
    // they're a planning partner and should be treated as such
    const planningSubscription = userInfo?.billing?.planningSubscription;
    const hasValidSubscription = planningSubscription && planningSubscription.id;

    // Planning partners will generally be invited to a project before they would start a trial.
    const isPlanningPartner = userInfo?.organization && !hasValidSubscription && loginWeddings.length > 0;

    // If user doesn't have payment method or is a planning partner, redirect to AccountSetup
    if (!hasPaymentMethod || isPlanningPartner) {
      // Redirect to AccountSetUp flow to create trial with payment info
      history.push('/app/v2/account-setup/step/1');
    } else {
      // For users with payment method, create trial immediately
      setStartingTrial(true);
      trial();
    }
  };

  const errorModalCloseHandler = () => {
    setErrorModalOpened(false);
    setErrorModalMessage(null);
  };

  const addPromoReferralCode = () => {
    addPromoOrReferral(promoReferralCode);
  };

  const onPlanSelect = ({ plan }) => {
    const { code } = plan;
    setSelectedPlan(plan);
    setSummaryScrollEnabled(true);
    fetchSeatsData(plan);

    // Load the invoice preview
    if (code && invoicePreviews && Object.prototype.hasOwnProperty.call(invoicePreviews, code)) {
      setInvoice(invoicePreviews[code]);
    } else if (currentPlan?.code === code) {
      setSelectedPlan(currentPlan);
      setInvoice(currentInvoice);
      clearBillingSummary();
      setSummaryScrollEnabled(false);
    }
  };

  const subscriptionConfirmationModalCloseHandler = () => {
    if (subscriptionUpdating) return;

    getBillingInfo();
    if (!subscribeError && seatsDiff > 0) {
      const seatsData = {
        additional_seat_count: additionalSeats,
        integration_enabled_logins: [],
      };
      updateSeats(orgId, seatsData);
    }
    setSubscriptionConfirmationModalOpened(false);
    summaryRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const cancelTrialComfirmationModalCloseHandler = (cancel) => {
    if (isTrial && cancel) {
      cancelTrial();
    }

    setCancelTrialConfirmationModalOpened(false);
  };

  const trialConversionConfirmationModalCloseHandler = (start) => {
    if (isTrial && start) {
      convertTrial();
    }

    setTrialConversionModalOpened(false);
  };

  const redirectContactUs = () => {
    clearBillingSummary();
    window.open('https://help.aisleplanner.com/en/articles/2126011-contact-us', '_blank');
  };

  const clearBillingSummary = () => {
    if (currentPlan) setPlanPrice(currentPlan.price);
  };

  const renderSubscriptionContent = () => {
    if (showNoPlanningSubscription || (!subscription && !isCancel)) {
      return (
        <div className="billing-subscription-plan__detail">
          <p>No Planning Subscription</p>
        </div>
      );
    }
    if (isPromo && expiresAt) {
      const date = dayjs(expiresAt ?? '').format('MMM. DD, YYYY');
      return (
        <div className="billing-subscription-plan__detail">
          {isExpired ? (
            <p className="billing-subscription-plan__error">Your promo ended <strong>{date}</strong></p>
          ) : (
            <p>Your free promo ends <strong>{date}</strong></p>
          )}
        </div>
      );
    }
    if (isTrial && expiresAt) {
      const date = dayjs(expiresAt ?? '').format('MMM. DD, YYYY');
      return (
        <div className="billing-subscription-plan__detail">
          {isExpired ? (
            <p className="billing-subscription-plan__error">Your trial ended <strong>{date}</strong></p>
          ) : (
            <p>Your free trial ends <strong>{date}</strong></p>
          )}
        </div>
      );
    };
    if (isPendingCancellation) {
      const date = dayjs(userInfo?.cancelAt).format('MMM. DD, YYYY');
      return (
        <div className="billing-subscription-plan__detail">
          <p className="billing-subscription-plan__error">
            Your plan will cancel on <strong>{date}</strong>.
          </p>
          <div>
            <p className="billing-subscription-plan__type">
              Plan: {planName}
            </p>
          </div>
        </div>
      );
    };
    if (isCancel) {
      const date = dayjs(userInfo?.cancelAt ?? endedAt).format('MMM. DD, YYYY');
      return (
        <div className="billing-subscription-plan__detail">
          <p className="billing-subscription-plan__error">Your plan was canceled on <strong>{date}</strong>
          </p>
        </div>
      );
    };
    if (isDelinquent && !isDisabled) {
      return (
        <div className="billing-subscription-plan__detail">
          <p className="billing-subscription-plan__error">Payment failed. Please update payment method below.</p>
          <div>
            <div>
              <p className="billing-subscription-plan__type">
                Plan: {planName}
              </p>
            </div>
          </div>
        </div>
      );
    };
    if (userInfo?.isPaused) {
      const date = dayjs(userInfo?.pausedAt ?? '').add(90, 'day').format('MMM. DD, YYYY');
      return (
        <div className="billing-subscription-plan__detail">
          {userInfo?.pausedAt ? (
            <p className="billing-subscription-plan__error">Account paused.
              Your plan will resume on <strong>{date}</strong></p>
          ) : (
            <p className="billing-subscription-plan__error">Account paused</p>
          )}
          <div>
            <div>
              <p className="billing-subscription-plan__type">
                Plan: {planName}
              </p>
            </div>
          </div>
        </div>
      );
    };
    if (invoiceDate) {
      const date = dayjs(invoiceDate ?? '').format('MMM. DD, YYYY');
      const expiryDate = remainingTrialDays ?
        dayjs().add(remainingTrialDays, 'day').format('MMM. DD, YYYY') : '';
      const showTrial = isTrial && remainingTrialDays;
      return (
        <div className="billing-subscription-plan__detail">
          <p>
            Your {showTrial ? 'free trial ends' : 'plan will renew on'} <strong>{showTrial ? expiryDate : date}</strong>
          </p>
          <div>
            <p className="billing-subscription-plan__type">
              Plan: {planName}
            </p>
          </div>
        </div>
      );
    }
  };

  const renderPaymentMethod = () => {
    const {
      paymentMethodType,
      paymentMethodName,
      paymentMethodLast4,
      paymentMethodExpMonth,
      paymentMethodExpYear,
    } = billingInfo;

    return (
      <>
        <div className="payment-method-title">Payment Method</div>
        <div className="payment-method">
          <div className={clsx('payment-method__image', paymentMethodName, `${paymentMethodType}-type`)} />
          <div className="payment-method__content">
            <div className="payment-method__item">{paymentMethodName}</div>
            <div className="payment-method__item">
              **** {paymentMethodLast4}
            </div>
            {paymentMethodType === 'card' ? (
              <div className="payment-method__item">
                Expires {paymentMethodExpMonth}/{paymentMethodExpYear}
              </div>
            ) : null}
          </div>
        </div>

        <button
          className="payment-method-edit"
          onClick={openModalPayment}
        >
          edit payment
        </button>
      </>
    );
  };

  const onUnsubscribe = () => {
    setCancelSubscriptionModalOpened(false);
    if (process.env.REACT_APP_PROFITWELL_ENABLED === 'true') {
      // eslint-disable-next-line no-undef
      profitwell('init_cancellation_flow').then((response) => {
        if (response.status === 'chose_to_cancel') {
          const cancelReason = response.cancelReason || 'Did not specify';
          unsubscribe(cancelReason);
          setSelectedPlan(null);
          clearBillingSummary();
        }
      });
    } else {
      unsubscribe();
      setSelectedPlan(null);
      clearBillingSummary();
    }
  };

  const onClear = () => {
    setSelectedPlan(currentPlan);
    setInvoice(currentInvoice);
    clearBillingSummary();
    setSummaryScrollEnabled(false);
    proPlansRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSeatsData = async (plan) => {
    const isChange = (plan && plan?.price !== 0 && subscription?.code !== plan?.code);
    if (!isChange) return;

    const [responseSeats, responseMembers] = await Promise.all([
      getSeats(orgId),
      getMembers(orgId),
    ]);

    if (responseMembers.ok && responseSeats.ok) {
      const { additional_seat_count } = responseSeats.data;
      setAdditionalSeats(additional_seat_count);
      const enabledCount = responseMembers.data.filter(member => member.integrations_enabled).length;

      const diff = enabledCount - (plan?.includedSeats + additional_seat_count);
      setSeatsDiff(diff);

      const isPlanUpgrade = isChange && plan?.price > currentPlan?.price;
      const updatedPlanSeats = plan?.includedSeats || 0;
      const currentPlanSeats = currentPlan?.includedSeats || 0;
      const isSeatsUpgrade = isChange && plan?.includedSeats > currentPlan?.includedSeats;
      const confirmationText = getSubscriptionConfirmationText(
        additional_seat_count,
        isPlanUpgrade,
        isSeatsUpgrade,
        updatedPlanSeats,
        currentPlanSeats,
        enabledCount,
      );
      setSubscriptionConfirmationText(confirmationText);
    }
  };

  const getSubscriptionConfirmationText = (
    memberAdditionalSeats,
    isPlanUpgrade,
    isSeatsUpgrade,
    updatedPlanSeats,
    currentPlanSeats,
    enabledSeats,
  ) => {
    let confirmationText = 'Thanks for being a bright spot in our day and the inspiration behind everything we do.';
    if (isTrial || !subscription) {
      confirmationText = `
        Thanks for being a bright spot in our day and the inspiration behind
        everything we do. Welcome to the Aisle Planner family.
      `;
    }

    if (!isPlanUpgrade && updatedPlanSeats < enabledSeats) {
      confirmationText = `
        You are currently using more AP Connect seats than your new plan supports.
        As a result, you will need to re-enable all AP Connect seats to keep your
        email and calendar integrations active. You can manage and purchase additional seats
        from your Subscription & Billing page.
      `;
    }

    if (isPlanUpgrade && isSeatsUpgrade && (memberAdditionalSeats > 0 || updatedPlanSeats > currentPlanSeats)) {
      confirmationText = `
        Did you know that your new plan includes additional AP Connect seats? We recommend that you
        review your current AP Connect seat subscriptions and assignments to ensure you’re making
        the most of your new plan. You can manage seats from your Subscription & Billing page.
      `;
    }

    return confirmationText;
  };

  const periodFieldName = proPlanTab === MONTHLY ? 'monthPremium' : 'yearPremium';

  if (isOrgMember) {
    return (
      <p className="org-member-message">
        Please contact the account owner for any questions or issues with your subscription.
      </p>
    );
  }

  if (isPromo && !expiresAt) {
    return (
      <div className="forever-promo-message">
        <h4>{planName}</h4>
        <p>This is a promotional account with no expiration date. No payment is required.</p>
      </div>
    );
  }

  const renderCancelSubscriptionModal = () => {
    return (
      <APModal
        open={cancelSubscriptionModalOpened}
        onClose={() => setCancelSubscriptionModalOpened(false)}
        title='Heads up!'
        name="cancel-subscription"
        actions={(
          <>
            <button
              onClick={() => {
                onUnsubscribe();
              }}
              className="button"
            >
              Confirm Cancellation
            </button>
            <button
              onClick={() => setCancelSubscriptionModalOpened(false)}
              className="button"
            >
              Keep my current plan
            </button>
          </>
        )}
      >
        <p>
          Cancellations are effective at the end of your current billing cycle. Please download everything you
          might need from your account before your subscription ends. If account access is needed after your
          cancellation is complete, payment will be required. Learn more about our cancellation terms&nbsp;
          <a
            href="https://help.aisleplanner.com/en/articles/1895250-canceling-your-subscription#h_3bc5b38f36"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          .
        </p>
      </APModal >
    );
  };

  const renderPauseSubscriptionModal = () => {
    return (
      <APModal
        open={pauseSubscriptionModalOpened}
        onClose={() => setPauseSubscriptionModalOpened(false)}
        title='Pause my account'
        name="pause-subscription"
        actions={(
          <>
            <button
              onClick={() => {
                onPauseAccount();
              }}
              className="button"
            >
              Pause my account
            </button>
            <button
              onClick={() => setPauseSubscriptionModalOpened(false)}
              className="button"
            >
              Keep my current plan
            </button>
          </>
        )}
      >
        <p>
          You can pause your account for up to 90 days at a time. While your account is paused, you and
          your planning partners will lose access to your projects and all paid features. Your subscription
          will automatically resume after 90 days, though you can manually resume at any time.

          Please note that you must maintain at least one month of PAID active subscription between pauses,
          and there are no refunds for the automatic renewal once your pause ends.<br />
          <a href="https://help.aisleplanner.com/en/articles/1895251-placing-your-subscription-on-hold"
            target="_blank"
            rel="noopener noreferrer"
          >
            Here's everything you need to know.
          </a>
        </p>
      </APModal>
    );
  };

  const renderSubscriptionConfirmationModal = () => {
    return (
      <APModal
        open={subscriptionConfirmationModalOpened}
        onClose={() => subscriptionConfirmationModalCloseHandler()}
        title={!isUpgrade && seatsDiff > 0 ? 'Heads up!' : 'You’re all set!'}
        actions={(
          <>
            {(paymentMethodUpdating || subscriptionUpdating) ? (
              <section
                className="app_loader"
                style={{ position: 'unset' }}
              >
                <FadeLoader
                  color={styledTheme.lighterGray}
                  loading
                  height={15}
                  width={5}
                  radius={5}
                  margin={2}
                />
              </section>
            ) : (
              <button
                onClick={() => subscriptionConfirmationModalCloseHandler()}
                className="button"
              >
                {
                  (!isUpgrade && seatsDiff > 0) || (isUpgrade && additionalSeats > 0) ?
                  'GOT IT' :
                  "Go get 'em, tiger!"
                }
              </button>
            )}
          </>
        )}
      >
        <p>{subscriptionConfirmationText}</p>
      </APModal>
    );
  };

  const renderCancelTrialModal = () => {
    return (
      <APModal
        open={cancelTrialConfirmationModalOpened}
        onClose={() => cancelTrialComfirmationModalCloseHandler()}
        title='Heads Up!'
        actions={(
          <>
            <button
              onClick={() => cancelTrialComfirmationModalCloseHandler(true)}
              className="button"
            >
              Cancel My Trial
            </button>
          </>
        )}
      >
        <p>
          Cancellations are effective immediately and you will no longer have access to your account.
          Please take a moment to download everything you might need
          from your account before canceling your subscription.
          Learn more about our cancellation terms {' '}
          <a
            href="https://help.aisleplanner.com/en/articles/1895250-canceling-your-subscription#h_3bc5b38f36"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          .
        </p>
      </APModal>
    );
  };

  const renderTrialConversionModal = () => {
    return (
      <APModal
        open={trialConversionConfirmationModalOpened}
        onClose={() => trialConversionConfirmationModalCloseHandler()}
        title='Subscribe During Your Free Trial'
        actions={(
          <>
            <button
              onClick={() => trialConversionConfirmationModalCloseHandler(true)}
              className="button"
            >
              Subscribe Now
            </button>
          </>
        )}
      >
        <p>
          You're still on your 30-day free trial, which will automatically convert to a paid subscription when it ends.
          If you subscribe now (for example, to add more AP Connect seats), your card will be charged immediately.
        </p>
      </APModal>
    );
  };

  return (
    <>
      {/* main page loader. if null, will be forever spinner */}
      {proPlans ? (
        <div>
          <APFormGroup
            direction="horizontal"
            className="billing-group"
          >
            <i className="billing-subscription__icon">
              <FontAwesomeIcon icon={['fas', 'bookmark']} />
            </i>
            <div className="billing-subscription-plan">
              {renderSubscriptionContent()}
              {isAwaitingMicrodeposit && (
                <div
                  className="billing-subscription-plan__error"
                >
                  Payment pending
                </div>
              )}
            </div>
          </APFormGroup>
          {showNoPlanningSubscription && (
            <APFormGroup
              title="No Planning Subscription"
              className="billing-group"
              direction="vertical"
            >
              <p className="billing-index__intro">
                While you don't need your own subscription to collaborate
                on a project you've been invited to, if you're an event pro and
                you'd like to take our pro tools for a test drive for your own business,
                start your free 30 day trial below. Learn more about Aisle Planner's
                sales, business and project management tools {' '}
                <a
                  href="https://aisleplanner.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>
                .
              </p>
              <div>
                <button
                  className="button"
                  disabled={isStartingTrial}
                  onClick={startTrial}
                >
                  {!isStartingTrial && <span>Start free trial</span>}
                  {isStartingTrial && (
                    <>
                      <span className="visually-hidden--mobile">Starting...</span>
                      <span className="fa fa-spin fa-spinner" />
                    </>
                  )}
                </button>
              </div>
            </APFormGroup>
          )}
          {showPromoCode && (
            <APFormGroup
              title="Promo Code"
              className="billing-group-add"
              direction="vertical"
            >
              {redeemedPromoCode ? (
                <p>Promo code applied! You're receiving {redeemedPromoCode}.</p>
              ) : (
                <>
                  <InputField
                    name="promo__referral_code"
                    label="Enter Code Before Subscribing Below."
                    sublabel="Promo codes are only valid on monthly plan subscriptions."
                    value={promoReferralCode}
                    onChange={(event) => {
                      setPromoReferralCode(event.target.value);
                      setPromoReferralCodeError(false);
                    }}
                    onBlur={() => {
                      addPromoReferralCode();
                    }}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    showError={showPromoReferralCodeError}
                  />
                  {showPromoReferralCodeError && (
                    <p className="billing-subscription-plan__error">This code is not valid.</p>
                  )}
                </>
              )}
            </APFormGroup>
          )}
          <div ref={proPlansRef}>
            <APFormGroup
              title="Pro Plans"
              className="billing-group"
            >
              <p className="billing-index__intro">
                All plans include our full suite of Sales and Business Management Tools.
                Project based plans also include our full suite of Project Management Tools
                designed to help you and your clients manage every aspect of the event
                planning journey. Choose a plan based on how many active projects you’ll
                be working on at any given time. Monthly plans are flexible so you can
                adjust your plan at any time to meet your current workload. Learn more
                about what’s included in all our plans{' '}
                {
                  // eslint-disable-next-line
                  <a href="https://help.aisleplanner.com/en/articles/1899152-overview-of-aisle-planner-plan-subscription-options"
                    target="_blank"
                    rel="noreferrer"
                  >
                    here
                  </a>
                }.
                <br /><br />
                All subscriptions are non-refundable. Applicable sales tax may apply.
              </p>
            </APFormGroup>
            <div className="tabs-container tabs-container--pricing-plans">
              <HorizontalMenu items={tabs} />
              <div className="billing-currency">$usd</div>
            </div>
            <div className="plans-container">
              {proPlans[periodFieldName].map((el, index, arr) => {
                // Don't display plans over 100 projects. Undefined is unlimited.
                if (!el.weddings || el.weddings < 15 || el.weddings > 101) {
                  // CRM only plan.
                  if (el.weddings === 1) {
                    return (
                      <PlanCard
                        price={`$${Number(el.price) / 100}`}
                        id={el.code}
                        period={proPlanTab === MONTHLY ? '/month' : '/year'}
                        savings={
                          proPlanTab === MONTHLY
                            ? ' Save 10% Annually'
                            : '10% savings'
                        }
                        selectedPlan={selectedPlan}
                        plan={el}
                        onSelect={onPlanSelect}
                        currentPlanProjects={!isCancel && currentNumberOfProjects}
                        currentInterval={currentInterval}
                        disabled={isPendingCancellation}
                        key={el.code}
                      />
                    );
                  }
                  return;
                }
                const prevWeddings = index === 0 ? null : arr[index - 1].weddings;
                // Since we don't want to explicitly advertise crm only as having 1 wedding, we still want
                // the first pro planning plan to say "Up to 15 Projects".
                const prevWeddingsCount = prevWeddings === 1 ? prevWeddings : prevWeddings + 1;
                const planText = (prevWeddings && prevWeddings >= 15) ?
                  `${prevWeddingsCount} - ${el.weddings}` : `up to ${el.weddings}`;
                return (
                  <PlanCard
                    price={`$${Number(el.price) / 100}`}
                    id={el.code}
                    projects={planText}
                    period={proPlanTab === MONTHLY ? '/month' : '/year'}
                    savings={
                      proPlanTab === MONTHLY
                        ? ' Save 10% Annually'
                        : '10% savings'
                    }
                    selectedPlan={selectedPlan}
                    plan={el}
                    onSelect={onPlanSelect}
                    currentPlanProjects={!isCancel && currentNumberOfProjects}
                    currentInterval={currentInterval}
                    disabled={isPendingCancellation}
                    key={el.code}
                  />
                );
              })}
              {proPlans[periodFieldName].length && (
                <PlanCard
                  price="Contact us"
                  id="PLAN-101+"
                  projects="101 +"
                  onSelect={redirectContactUs}
                  currentPlanCode={subscription?.code}
                  plan={{
                    code: 'PLAN-101+',
                    price: 0,
                    includedSeats: 5,
                  }}
                  // We don't want this is be selected so manually input number so it does not match.
                  currentPlanProjects={9999}
                  disabled={isPendingCancellation}
                />
              )}
            </div>

            <APFormGroup
              title="AP Connect"
              className="billing-group"
            >
              <p className="billing-index__intro">
                Seamlessly integrate your third party email and calendar within the Aisle Planner platform
                with AP Connect. Send and receive client emails directly through your client's lead record,
                create scheduling links so your clients can book time with you during designated timeframes,
                and have a single view of all action items on your calendar--captured through
                Aisle Planner, as well as your third party calendar platform. Managing your business and your
                clients just got easier with AP Connect.&nbsp;
                <a href="https://help.aisleplanner.com/en/articles/10375834-ap-connect"
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more
                </a>.
                <br /><br />
                {showManageSeats && (
                  <button
                    onClick={() => setSeatsModalIsOpen(true)}
                    className="button button--manage-seats"
                  >
                    MANAGE SEATS
                  </button>
                )}
              </p>
            </APFormGroup>
          </div>
          {showBillingSummary && (
            <>
              {/*
                * #TODO: this logic could probably be cleaned up, but we don't want to show invoice preview
                * before the previews are fetched and loaded.
              */}
              {isPlanChange && (subscription && !invoicePreviews) ? (
                <section
                  ref={summaryRef}
                  className="billing-summary billing-summary--loading"
                >
                  <div className="summary">
                    <FadeLoader
                      className="billing-summary__loader"
                      color={styledTheme.lighterGray}
                      loading
                      height={15}
                      width={5}
                      radius={5}
                      margin={2}
                    />
                  </div>
                </section>
              ) : (
                <BillingSummary
                  ref={summaryRef}
                  selectedPlan={selectedPlan}
                  planPrice={planPrice}
                  billingCycle={billingCycle}
                  subscription={subscription}
                  invoice={invoice}
                  plans={proPlans}
                  startingBalance={startingBalance}
                  creditApplied={creditApplied}
                  redeemedPromoCode={redeemedPromoCode}
                  isTrial={isTrial}
                  isPromo={isPromo}
                  isExpired={isExpired}
                  expiresAt={expiresAt}
                  remainingTrialDays={remainingTrialDays}
                  isPlanChange={isPlanChange}
                  isIntervalChange={isIntervalChange}
                  isUpgrade={isUpgrade}
                  isCancel={isCancel}
                  isDelinquent={isDelinquent}
                  isPendingCancellation={isPendingCancellation}
                  onSubscribe={onSubscribe}
                  onClear={onClear}
                />
              )}
            </>
          )}
          {showPaymentMethod ? renderPaymentMethod() : null}
          {showPauseSubscription && (
            <APFormGroup
              className="billing-group"
              direction="vertical"
            >
              <div className="subscription-pause-buttons">
                <button
                  className="button"
                  onClick={() => setCancelSubscriptionModalOpened(true)}
                >
                  Cancel Subscription
                </button>
                <button
                  className={clsx('button', {
                    disabled: subscriptionPauseUpdating,
                  })}
                  onClick={onPauseSubscription}
                >
                  {!subscriptionPauseUpdating && (
                    userInfo?.isPaused ? (
                      'Unpause Subscription'
                    ) : (
                      'Pause Subscription'
                    ))}
                  {subscriptionPauseUpdating && (
                    <>
                      {userInfo?.isPaused ? (
                        <span className="visually-hidden--mobile">Unpausing Subscription</span>
                      ) : (
                        <span className="visually-hidden--mobile">Pausing Subscription</span>
                      )}
                      <span className="fa fa-spin fa-spinner" />
                    </>
                  )}
                </button>
              </div>
            </APFormGroup>
          )}
          {isPendingCancellation && (
            <APFormGroup
              className="billing-group"
              direction="vertical"
            >
              <div className="subscription-pause-buttons">
                <button
                  className={clsx('button', { disabled: subscriptionPauseUpdating })}
                  onClick={resumeSubscription}
                  disabled={subscriptionPauseUpdating}
                >
                  {subscriptionPauseUpdating ? (
                    <>
                      <span className="visually-hidden--mobile">Resubscribing</span>
                      <span className="fa fa-spin fa-spinner" />
                    </>
                  ) : 'Resubscribe Account'}
                </button>
              </div>
            </APFormGroup>
          )}
          {showTrialConversion && (
            <APFormGroup
              className="billing-group"
              direction="vertical"
            >
              <div className="trial-conversion-buttons">
                <button
                  className="button"
                  onClick={() => setCancelTrialConfirmationModalOpened(true)}
                >
                  Cancel Trial
                </button>
                <button
                  className={clsx('button', {
                    disabled: trialConversionConfirmationModalOpened || !selectedPlan,
                  })}
                  onClick={() => setTrialConversionModalOpened(true)}
                >
                  {!trialConversionConfirmationModalOpened && 'Start Paid Subscription'}
                  {trialConversionConfirmationModalOpened && (
                    <>
                      <span className="visually-hidden--mobile">Starting Subscription</span>
                      <span className="fa fa-spin fa-spinner" />
                    </>
                  )}
                </button>
              </div>
            </APFormGroup>
          )}
          {showBillingFooter && (
            <APFormGroup
              className="billing-group"
              direction="vertical"
            >
              <p className="billing-index__intro">
                Subscriptions are automatically renewed and billed on a monthly or annual basis
                until you cancel. Annual subscriptions are paid up front and are based on a
                one-year commitment. Applicable sales tax may apply based on your billing location.
                You may change or cancel your subscription at any
                time. All subscription payments are non-refundable. {' '}
                <a
                  href="https://www.aisleplanner.com/legal#terms"
                  target="_blank"
                  rel="noreferrer"
                >
                  Aisle Planner terms
                </a> apply. Resubscribing during your cancellation period will revert your account
                to your previously active plan and billing cycle.
              </p>
              <p className="billing-index__intro">
                For questions on plans, pricing, or billing, please visit our {' '}
                <a
                  href="https://help.aisleplanner.com/en/collections/72399-subscriptions-account-settings"
                  target="_blank"
                  rel="noreferrer"
                >
                  help center
                </a> for answers to our most commonly asked questions. You can also connect
                with us through the messenger to your right or email us at {' '}
                <a
                  href="mailto:customercare@aisleplanner.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  customercare@aisleplanner.com
                </a>
                .
              </p>
            </APFormGroup >
          )}
        </div >
      ) : (
        <section
          className="app_loader"
          style={{ marginTop: '250px', position: 'unset' }}
        >
          <FadeLoader
            color={styledTheme.lighterGray}
            loading
            height={15}
            width={5}
            radius={5}
            margin={2}
          />
        </section>
      )}

      <PaymentModal
        open={modalPaymentIsOpen}
        onClose={() => setModalPaymentIsOpen(false)}
      />
      <SeatsModal
        open={seatsModalIsOpen}
        onClose={() => setSeatsModalIsOpen(false)}
        orgId={orgId}
        currentPlan={currentPlan}
        planType={selectedPlan?.renew}
        handleSeatsUpdate={handleSeatsUpdate}
      />
      <APModal
        open={seatsUpdated}
        onClose={() => setSeatsUpdated(false)}
        title="You’re All Set!"
        actions={(
          <button
            onClick={() => setSeatsUpdated(false)}
            className="button"
          >
            Got it
          </button>
        )}
      >
        <p>
          If you’ve enabled additional team members, we’ll send them a notification to let them know,
          along with more information on AP Connect and how they can get started.
        </p>
      </APModal>
      <APModal
        open={errorModalOpened}
        onClose={errorModalCloseHandler}
        actions={(
          <button
            onClick={errorModalCloseHandler}
            className="button"
          >
            Ok
          </button>
        )}
      >
        <div>{errorModalMessage}</div>
      </APModal>
      {renderSubscriptionConfirmationModal()}
      {renderPauseSubscriptionModal()}
      {renderCancelSubscriptionModal()}
      {renderCancelTrialModal()}
      {renderTrialConversionModal()}
    </>
  );
};

export default Billing;
